import React, { useState } from 'react';
import { Play, ExternalLink, Youtube } from 'lucide-react';

/**
 * YouTubeEmbed — façade « lite-youtube » + iframe nocookie.
 *
 * Stratégie RGPD-friendly :
 *  1) Affichage initial = miniature statique (i.ytimg.com → image, pas de cookie).
 *  2) Clic sur "Play" → injection de l'iframe `youtube-nocookie.com` (mode privacy
 *     enhanced, aucun cookie tant que l'utilisateur ne joue pas une vidéo).
 *  3) Crédit de la chaîne visible avec lien sortant.
 *
 * Cf. CNIL : https://www.cnil.fr/fr/cookies-traceurs-que-dit-la-loi
 */
export function YouTubeEmbed({
  videoId,
  title,
  channel,
  channelHandle,
  duration,
  className = '',
  testId,
}) {
  const [loaded, setLoaded] = useState(false);

  // Miniatures YouTube (i.ytimg.com sert des images statiques sans cookie)
  // hqdefault.jpg = 480×360 (toujours dispo) ; maxresdefault.jpg pas garanti.
  const thumbHQ = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  const thumbMax = `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;

  // Mode privacy-enhanced (youtube-nocookie.com)
  const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;

  const channelUrl = channelHandle
    ? `https://youtube.com/${channelHandle}`
    : null;

  return (
    <div
      className={`mc-card overflow-hidden ${className}`}
      data-testid={testId || `yt-embed-${videoId}`}
    >
      <div className="relative aspect-video bg-black">
        {loaded ? (
          <iframe
            src={embedUrl}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            className="absolute inset-0 w-full h-full border-0"
            data-testid={`yt-iframe-${videoId}`}
          />
        ) : (
          <button
            type="button"
            onClick={() => setLoaded(true)}
            className="group absolute inset-0 w-full h-full flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] focus-visible:ring-offset-2"
            aria-label={`Lancer la vidéo : ${title}`}
            data-testid={`yt-play-${videoId}`}
          >
            <picture>
              <source srcSet={thumbMax} media="(min-width: 768px)" />
              <img
                src={thumbHQ}
                alt={`Miniature de la vidéo : ${title}`}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => {
                  // fallback si maxresdefault n'existe pas
                  if (e.currentTarget.src !== thumbHQ) e.currentTarget.src = thumbHQ;
                }}
              />
            </picture>
            {/* Voile sombre */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/30 group-hover:from-black/70 transition-colors" />
            {/* Bouton play */}
            <span className="relative z-10 inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-600 text-white shadow-2xl group-hover:scale-110 group-active:scale-100 transition-transform">
              <Play className="w-7 h-7 sm:w-9 sm:h-9 ml-1" fill="currentColor" strokeWidth={0} />
            </span>
            {/* Durée */}
            {duration && (
              <span className="absolute bottom-2 right-2 z-10 inline-flex items-center bg-black/85 text-white rounded px-1.5 py-0.5 text-[11px] font-bold tabular-nums">
                {duration}
              </span>
            )}
            {/* Badge YouTube */}
            <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-sm text-foreground rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest">
              <Youtube className="w-3 h-3 text-red-600" /> YouTube
            </span>
          </button>
        )}
      </div>

      {/* Métadonnées sous le player */}
      <div className="p-3 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-bold text-[13.5px] leading-tight tracking-tight line-clamp-2">
            {title}
          </div>
          {channel && (
            <a
              href={channelUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors mt-1"
              data-testid={`yt-channel-${videoId}`}
            >
              <span>Chaîne · {channel}</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default YouTubeEmbed;
